import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Input,
  Select,
  SimpleGrid,
  useToast,
  Checkbox,
  Textarea,
  Text
} from "@chakra-ui/react";
import { createClientContact, updateClientContact, createContactWithoutCompany } from "../../../api/clientsApi";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { useTranslation } from "react-i18next";
import {
  CONTACT_STATUSES,
  CONTACT_STATUS_LABELS,
  CONTACT_STATUS_LABEL_KEYS,
  getStatusLabel
} from "../../../constants/statuses";
import { FieldLabel } from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX
} from "../../../components/Portal/portalModalChrome";

const DEFAULT_NEW_CONTACT_STATUS = "new_lead_pool";

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  linkedin_url: "",
  title: "",
  division: "",
  comments: "",
  status: DEFAULT_NEW_CONTACT_STATUS,
  is_active: true
};

const ContactFormModal = ({ isOpen, onClose, initialData, clientId, onSuccess }) => {
  const { selectedBusinessUnitId } = useBusinessUnit();
  const [form, setForm] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const toast = useToast();
  const { t } = useTranslation("portal");

  const isEdit = Boolean(initialData?.id);

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && initialData) {
      setForm({
        name: initialData?.name || "",
        email: initialData?.email || "",
        phone: initialData?.phone || "",
        linkedin_url:
          initialData?.linkedin_url || initialData?.linkedinUrl || "",
        title: initialData?.title || "",
        division: initialData?.division || "",
        comments: initialData?.comments || "",
        status: initialData?.status || "",
        is_active:
          initialData?.is_active != null
            ? Boolean(initialData.is_active)
            : initialData?.active != null
            ? Boolean(initialData.active)
            : true
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [initialData, isEdit, isOpen]);

  const isValid = useMemo(() => {
    return form.name.trim().length > 0;
  }, [form.name]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        linkedin_url: form.linkedin_url.trim() || undefined,
        title: form.title.trim() || undefined,
        division: form.division.trim() || undefined,
        comments: form.comments.trim() || undefined,
        status: form.status || undefined,
        is_active: Boolean(form.is_active)
      };

      if (isEdit) {
        const updated = await updateClientContact(clientId, initialData.id, payload, { buId: selectedBusinessUnitId || undefined });
        toast({ title: t("pilotClients.contactsForm.toasts.updated"), status: "success" });
        onSuccess?.(updated);
      } else if (clientId == null) {
        // Item 2 of ASAP_PILOT_0305: contact without parent company is
        // attached to the per-BU "Unknown" placeholder server-side.
        const created = await createContactWithoutCompany(payload, { buId: selectedBusinessUnitId || undefined });
        toast({ title: t("pilotClients.contactsForm.toasts.created"), status: "success" });
        onSuccess?.(created);
      } else {
        const created = await createClientContact(clientId, payload, { buId: selectedBusinessUnitId || undefined });
        toast({ title: t("pilotClients.contactsForm.toasts.created"), status: "success" });
        onSuccess?.(created);
      }
      onClose?.();
    } catch (e) {
      toast({
        title: t("pilotClients.contactsForm.toasts.operationFailedTitle"),
        description:
          e?.response?.data?.message ||
          t("pilotClients.contactsForm.toasts.operationFailedDescription"),
        status: "error"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay bg="rgba(11,31,58,.45)" />
      <ModalContent sx={MODAL_CONTENT_SX}>
        <ModalHeader sx={MODAL_HEADER_SX}>
          {isEdit
            ? t("pilotClients.contactsForm.modalTitleEdit")
            : t("pilotClients.contactsForm.modalTitleCreate")}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py="16px">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="16px" spacingY="12px">
            <Box>
              <FieldLabel isRequired>{t("pilotClients.contactsForm.fields.name")}</FieldLabel>
              <Input
                size="sm"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder={t("pilotClients.contactsForm.placeholders.name")}
                isRequired
                aria-required={true}
              />
            </Box>
            <Box>
              <FieldLabel>{t("pilotClients.contactsForm.fields.status")}</FieldLabel>
              <Select
                size="sm"
                name="status"
                value={form.status}
                onChange={handleChange}
                placeholder={t("pilotClients.contactsForm.placeholders.status")}
              >
                {CONTACT_STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {getStatusLabel(t, CONTACT_STATUS_LABEL_KEYS, CONTACT_STATUS_LABELS, s.value)}
                  </option>
                ))}
              </Select>
            </Box>
            <Box>
              <FieldLabel>{t("pilotClients.contactsForm.fields.email")}</FieldLabel>
              <Input
                size="sm"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder={t("pilotClients.contactsForm.placeholders.email")}
              />
            </Box>
            <Box>
              <FieldLabel>{t("pilotClients.contactsForm.fields.phone")}</FieldLabel>
              <Input
                size="sm"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder={t("pilotClients.contactsForm.placeholders.phone")}
              />
            </Box>
            <Box>
              <FieldLabel>{t("pilotClients.contactsForm.fields.linkedinUrl")}</FieldLabel>
              <Input
                size="sm"
                name="linkedin_url"
                type="url"
                value={form.linkedin_url}
                onChange={handleChange}
                placeholder={t("pilotClients.contactsForm.placeholders.linkedinUrl")}
              />
            </Box>
            <Box>
              <FieldLabel>{t("pilotClients.contactsForm.fields.title")}</FieldLabel>
              <Input
                size="sm"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder={t("pilotClients.contactsForm.placeholders.title")}
              />
            </Box>
            <Box>
              <FieldLabel>{t("pilotClients.contactsForm.fields.division")}</FieldLabel>
              <Input
                size="sm"
                name="division"
                value={form.division}
                onChange={handleChange}
                placeholder={t("pilotClients.contactsForm.placeholders.division")}
              />
            </Box>
            <Box display="flex" alignItems="flex-end" pb="6px">
              <Checkbox
                size="sm"
                name="is_active"
                isChecked={Boolean(form.is_active)}
                onChange={handleChange}
              >
                <Text as="span" fontSize="13px" sx={{ marginBottom: "0 !important" }}>
                  {t("pilotClients.contactsForm.fields.active")}
                </Text>
              </Checkbox>
            </Box>
          </SimpleGrid>
          <Box mt="12px">
            <FieldLabel>{t("pilotClients.contactsForm.fields.comments")}</FieldLabel>
            <Textarea
              size="sm"
              name="comments"
              value={form.comments}
              onChange={handleChange}
              placeholder={t("pilotClients.contactsForm.placeholders.comments")}
            />
          </Box>
        </ModalBody>
        <ModalFooter sx={MODAL_FOOTER_SX}>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t("pilotClients.contactsForm.buttons.cancel")}
          </Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} isDisabled={!isValid} isLoading={isSubmitting}>
            {isEdit
              ? t("pilotClients.contactsForm.buttons.saveChanges")
              : t("pilotClients.contactsForm.buttons.create")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ContactFormModal;
